import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import AllergenBadge from "@/components/AllergenBadge";
import { useNavigate } from "react-router-dom";
import { ScanLine, Plus, Minus, Trash2, LogOut, CheckCircle2, XCircle, Wallet, Bell, AlertTriangle, WifiOff, Wifi, Moon, FileText, Sparkles } from "lucide-react";

export default function POS() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [menu, setMenu] = useState([]);
  const [cats, setCats] = useState(["All"]);
  const [activeCat, setActiveCat] = useState("All");
  const [cart, setCart] = useState({});
  const [adhocCart, setAdhocCart] = useState([]);
  const [adhocModal, setAdhocModal] = useState(null);
  const [queue, setQueue] = useState([]);
  const [tapInput, setTapInput] = useState("");
  const [tapResult, setTapResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [eligibility, setEligibility] = useState(null);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [dayEnd, setDayEnd] = useState(null);
  const [pendingOffline, setPendingOffline] = useState(() => {
    try { return JSON.parse(localStorage.getItem("grubpe_pos_pending") || "[]"); } catch { return []; }
  });

  // Persist offline queue
  useEffect(() => {
    localStorage.setItem("grubpe_pos_pending", JSON.stringify(pendingOffline));
  }, [pendingOffline]);

  // Cache menu for offline use
  useEffect(() => {
    api.get("/menu").then(r => { setMenu(r.data); localStorage.setItem("grubpe_pos_menu", JSON.stringify(r.data)); }).catch(() => {
      try { setMenu(JSON.parse(localStorage.getItem("grubpe_pos_menu") || "[]")); } catch {}
    });
    api.get("/menu/categories").then(r => setCats(["All", ...r.data])).catch(()=>{});
    const tick = () => {
      api.get("/orders/queue").then(r => setQueue(r.data)).catch(()=>{});
    };
    tick();
    const t = setInterval(tick, 5000);
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener("online", onUp);
    window.addEventListener("offline", onDown);
    return () => { clearInterval(t); window.removeEventListener("online", onUp); window.removeEventListener("offline", onDown); };
  }, []);

  // Auto-sync when back online
  useEffect(() => {
    if (online && pendingOffline.length > 0) {
      syncOffline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const filtered = useMemo(
    () => menu.filter(m => {
      if (activeCat !== "All" && m.category !== activeCat) return false;
      if (searchQuery && !m.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    }),
    [menu, activeCat, searchQuery]
  );

  const cartItems = useMemo(() => Object.entries(cart).map(([id, qty]) => {
    const m = menu.find(x => x.id === id);
    return m ? { ...m, qty } : null;
  }).filter(Boolean), [cart, menu]);
  const menuTotal = cartItems.reduce((s, c) => s + c.price * c.qty, 0);
  const adhocTotal = adhocCart.reduce((s, a) => s + (a.price * (1 + (a.tax_rate || 0) / 100)) * a.qty, 0);
  const total = menuTotal + adhocTotal;

  const inc = (id) => setCart(c => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart(c => { const n = {...c}; n[id] = (c[id]||0)-1; if (n[id]<=0) delete n[id]; return n; });

  const addAdhoc = () => {
    if (!adhocModal?.name?.trim() || !adhocModal?.price) return toast.error("Name and price required");
    setAdhocCart(a => [...a, {
      name: adhocModal.name.trim(),
      qty: Number(adhocModal.qty || 1),
      price: Number(adhocModal.price),
      tax_rate: Number(adhocModal.tax_rate || 0),
    }]);
    setAdhocModal(null);
  };

  const checkEligibility = async (rfid) => {
    if (!rfid || !online) { setEligibility(null); return; }
    try {
      const { data } = await api.post("/rfid/eligibility", { rfid_card_id: rfid });
      setEligibility(data);
    } catch { setEligibility(null); }
  };

  const tap = async (rfid) => {
    if (!rfid) return;
    const items = cartItems.map(c => ({ menu_item_id: c.id, qty: c.qty }));
    const adhoc_items = adhocCart.map(a => ({ name: a.name, qty: a.qty, price: a.price, tax_rate: a.tax_rate }));
    // auto: meal_plan if cart empty, else pos
    const mode = (items.length === 0 && adhoc_items.length === 0) ? "meal_plan" : "pos";
    if (!online) {
      const offlineRecord = { id: Date.now(), rfid_card_id: rfid, items, adhoc_items, mode, queued_at: new Date().toISOString() };
      setPendingOffline(p => [...p, offlineRecord]);
      setTapResult({ accepted: true, offline: true, student: { name: `Card ${rfid}` }, message: "Queued offline · will sync when online" });
      setCart({}); setAdhocCart([]); setTapInput(""); setEligibility(null);
      setTimeout(() => setTapResult(null), 5000);
      return;
    }
    try {
      const { data } = await api.post("/rfid/tap", { rfid_card_id: rfid, items, adhoc_items, mode });
      setTapResult(data);
      if (data.accepted) {
        toast.success(data.meal_plan ? "Meal plan redeemed" : "Charged to wallet");
        setCart({}); setAdhocCart([]); setEligibility(null);
        api.get("/orders/queue").then(r => setQueue(r.data));
      } else {
        toast.error(data.reason || "Rejected");
      }
      setTapInput("");
      setTimeout(() => setTapResult(null), 7000);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const syncOffline = async () => {
    if (pendingOffline.length === 0) return;
    let ok = 0, fail = 0;
    const failed = [];
    for (const rec of pendingOffline) {
      try {
        const { data } = await api.post("/rfid/tap", { rfid_card_id: rec.rfid_card_id, items: rec.items });
        if (data.accepted) ok++;
        else { fail++; failed.push({ ...rec, error: data.reason }); }
      } catch (e) {
        fail++; failed.push({ ...rec, error: formatErr(e) });
      }
    }
    setPendingOffline(failed);
    toast.success(`Synced ${ok} offline taps${fail ? ` · ${fail} failed` : ""}`);
    api.get("/orders/queue").then(r => setQueue(r.data));
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/orders/${id}/status`, { status }); api.get("/orders/queue").then(r => setQueue(r.data)); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const runDayEnd = async () => {
    if (!window.confirm("Close out today? All pending/preparing/ready orders will be marked completed.")) return;
    try {
      const { data } = await api.post("/admin/reports/pos/day-end");
      setDayEnd(data);
      api.get("/orders/queue").then(r => setQueue(r.data));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const allergenWarning = cartItems.flatMap(i => i.dietary_tags || []).filter(t => ["nuts", "dairy", "egg"].includes(t));

  return (
    <div className="w-screen h-screen overflow-hidden flex flex-col bg-cream">
      <div className="h-14 bg-ink text-white flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <BrandLogo size="sm" className="text-white" />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-sun bg-white/10 px-2 py-0.5 rounded-full">POS Terminal</span>
          <span data-testid="pos-online-badge" className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${online ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/30 text-amber-200'}`}>
            {online ? <Wifi size={10} /> : <WifiOff size={10} />} {online ? "Online" : "Offline"}
          </span>
          {pendingOffline.length > 0 && (
            <button data-testid="pos-sync-offline" onClick={syncOffline}
              className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-amber-300 text-amber-900 inline-flex items-center gap-1">
              {pendingOffline.length} pending · sync now
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="pos-day-end" onClick={runDayEnd} className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold inline-flex items-center gap-1">
            <Moon size={12} /> Day-end
          </button>
          <span className="text-sm hidden sm:inline">{user?.name}</span>
          <button data-testid="pos-logout" onClick={() => { logout(); nav("/"); }} className="p-2 rounded-full hover:bg-white/10"><LogOut size={16} /></button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-2/3 h-full overflow-y-auto p-4">
          <div className="mb-3">
            <input data-testid="pos-search" type="text" placeholder="Search items…"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full rounded-xl border-2 border-border px-4 py-2.5 text-sm font-semibold focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 bg-white" />
          </div>
          <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar">
            {cats.map(c => (
              <button key={c} onClick={() => setActiveCat(c)} data-testid={`pos-cat-${c}`}
                className={`px-4 py-3 rounded-xl text-base font-bold whitespace-nowrap transition-colors ${activeCat === c ? "bg-ink text-white" : "bg-cream-200 text-ink-700 hover:bg-sun"}`}>
                {c}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {filtered.map(m => (
              <button key={m.id} data-testid={`pos-item-${m.id}`} disabled={m.sold_out} onClick={() => inc(m.id)}
                className="bg-white border-2 border-border rounded-2xl p-3 flex flex-col items-start justify-between h-32 hover:border-brand active:scale-95 transition-all shadow-sm disabled:opacity-40 text-left relative">
                {m.is_mrp && <span className="absolute top-1 right-1 text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-sun text-ink">MRP</span>}
                <div className="font-heading font-bold text-ink leading-tight">{m.name}</div>
                <div className="flex items-center justify-between w-full">
                  <div className="flex flex-wrap gap-1">{m.dietary_tags?.slice(0, 2).map(t => <AllergenBadge key={t} tag={t} />)}</div>
                  <div className="font-heading text-xl font-extrabold text-brand">{inr(m.price)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="w-1/3 h-full bg-white border-l border-border flex flex-col shadow-pos">
          <div className="p-5 border-b border-border bg-gradient-to-br from-cream to-cream-200">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-2">RFID Tap</div>
            <form onSubmit={(e) => { e.preventDefault(); tap(tapInput); }} className="flex gap-2">
              <input data-testid="rfid-tap-input" value={tapInput} onChange={e => { setTapInput(e.target.value); checkEligibility(e.target.value); }}
                placeholder="Tap or type card ID..."
                className="flex-1 rounded-xl border-2 border-border px-4 py-3 text-base font-mono focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" autoFocus />
              <button type="submit" className="px-4 rounded-xl bg-brand text-white font-bold hover:bg-brand-600 active:scale-95"><ScanLine size={18} /></button>
            </form>
            <div className="mt-2 flex flex-wrap gap-1">
              {["RFID-001", "RFID-002"].map(r => (
                <button key={r} data-testid={`pos-quick-${r}`} onClick={() => tap(r)}
                  className="text-[11px] px-2 py-1 rounded-full bg-white border border-border font-mono hover:bg-cream-200">
                  {r}
                </button>
              ))}
            </div>
            {tapResult && (
              <div className={`mt-3 rounded-xl p-3 border ${tapResult.accepted ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="pos-tap-result">
                <div className="flex items-center gap-2">
                  {tapResult.accepted ? <CheckCircle2 className="text-emerald-700" size={18} /> : <XCircle className="text-brand" size={18} />}
                  <div className="font-heading font-bold text-ink">
                    {tapResult.accepted ? `${tapResult.offline ? "Queued offline" : "Accepted"} · ${tapResult.student?.name}` : `Rejected · ${tapResult.reason}`}
                  </div>
                </div>
                {tapResult.accepted && tapResult.meal_plan && (
                  <div className="text-xs text-emerald-800 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900 text-[10px] font-bold">MEAL PLAN</span>
                    <span>{tapResult.plan?.name} · {tapResult.slot} redeemed · valid until {tapResult.plan?.expires_on}</span>
                  </div>
                )}
                {tapResult.accepted && !tapResult.meal_plan && tapResult.balance !== undefined && (
                  <div className="text-xs text-ink-600 mt-1 flex items-center gap-2">
                    <Wallet size={12} /> Wallet balance: {inr(tapResult.balance)}
                  </div>
                )}
                {!tapResult.accepted && tapResult.code === "no_meal_plan" && (
                  <div className="text-xs text-red-700 mt-1 italic">Ask the parent to subscribe to a meal plan, or charge items to wallet instead.</div>
                )}
                {!tapResult.accepted && tapResult.code === "slot_already_redeemed" && (
                  <div className="text-xs text-amber-700 mt-1 italic">This card already took {tapResult.slot} today — only one {tapResult.slot} per card per day.</div>
                )}
              </div>
            )}
            {eligibility?.known && !tapResult && (
              <div className="mt-3 rounded-xl p-3 border border-border bg-cream-100" data-testid="pos-eligibility">
                <div className="text-[11px] font-bold uppercase tracking-wider text-ink-500">{eligibility.student.name} · {eligibility.student.grade}</div>
                {eligibility.is_subscriber ? (
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className="px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900 text-[10px] font-bold">MEAL PLAN</span>
                    <span className="text-xs text-emerald-900">{eligibility.plan?.name} · valid until {eligibility.plan?.end_date}</span>
                    {eligibility.plan?.expiring_soon && <span className="px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-[10px] font-bold">EXPIRING · {eligibility.plan.days_left}d left</span>}
                    {eligibility.plan?.already_redeemed_today_slot && <span className="px-1.5 py-0.5 rounded bg-red-200 text-red-900 text-[10px] font-bold">{eligibility.plan.current_slot.toUpperCase()} USED</span>}
                  </div>
                ) : (
                  <div className="mt-1 flex items-center gap-2">
                    <Wallet size={12} className="text-ink-500"/>
                    <span className="text-xs text-ink-700">Wallet balance: <b>{inr(eligibility.wallet_balance)}</b></span>
                    <span className="text-[10px] text-ink-500 italic">(no meal plan)</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-5 border-b border-border flex-1 overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="font-heading font-bold text-ink">Order</div>
              <div className="flex items-center gap-2">
                <button data-testid="pos-add-adhoc" onClick={() => setAdhocModal({ name: "", qty: 1, price: "", tax_rate: 5 })}
                  className="text-xs font-bold text-brand hover:text-brand-700 inline-flex items-center gap-1">
                  <Plus size={12}/> Add ad-hoc item
                </button>
                {(cartItems.length > 0 || adhocCart.length > 0) && <button onClick={() => { setCart({}); setAdhocCart([]); }} className="text-xs text-ink-500 hover:text-brand">Clear</button>}
              </div>
            </div>
            {(cartItems.length + adhocCart.length) === 0 ? (
              <div className="text-sm text-ink-500 mt-3">Tap card for meal-plan redemption, or pick items &amp; tap to charge wallet.</div>
            ) : (
              <div className="mt-3 space-y-2">
                {cartItems.map(c => (
                  <div key={c.id} className="flex items-center gap-2" data-testid={`pos-cart-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-ink truncate">{c.name}</div>
                      <div className="text-xs text-ink-500">{inr(c.price)}{c.is_mrp ? " · MRP" : ` + ${c.tax_rate || 0}%`}</div>
                    </div>
                    <button onClick={() => dec(c.id)} className="w-8 h-8 rounded-full bg-cream-200 hover:bg-cream-300 flex items-center justify-center"><Minus size={14} /></button>
                    <span className="font-bold w-5 text-center">{c.qty}</span>
                    <button onClick={() => inc(c.id)} className="w-8 h-8 rounded-full bg-ink text-white flex items-center justify-center"><Plus size={14} /></button>
                    <div className="font-bold w-14 text-right">{inr(c.price * c.qty)}</div>
                  </div>
                ))}
                {adhocCart.map((a, idx) => (
                  <div key={`ad-${idx}`} className="flex items-center gap-2" data-testid={`pos-adhoc-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-ink truncate inline-flex items-center gap-1.5">
                        {a.name} <Sparkles size={10} className="text-amber-600"/>
                      </div>
                      <div className="text-xs text-ink-500">₹{a.price} + {a.tax_rate}% · ad-hoc</div>
                    </div>
                    <span className="font-bold w-5 text-center">{a.qty}</span>
                    <button onClick={() => setAdhocCart(x => x.filter((_, i) => i !== idx))} className="w-8 h-8 rounded-full bg-red-50 text-brand hover:bg-red-100 flex items-center justify-center"><Trash2 size={12}/></button>
                    <div className="font-bold w-14 text-right">{inr(a.price * (1 + a.tax_rate/100) * a.qty)}</div>
                  </div>
                ))}
              </div>
            )}
            {allergenWarning.length > 0 && (
              <div className="mt-3 flex items-center gap-2 text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs">
                <AlertTriangle size={14} /> Contains: {[...new Set(allergenWarning)].join(", ").toUpperCase()}
              </div>
            )}
          </div>

          <div className="p-5 border-t border-border bg-cream-100">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Total</span>
              <span className="font-heading text-3xl font-extrabold text-ink" data-testid="pos-total">{inr(total)}</span>
            </div>
            <div className="mt-2 text-[11px] text-ink-500">Tax computed per item · MRP items are inclusive · Total auto-charges on RFID tap.</div>
          </div>
        </div>
      </div>

      <div className="h-32 bg-ink text-white flex-shrink-0 flex flex-col">
        <div className="px-4 pt-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-sun"><Bell size={12} /> Live Queue · {queue.length}</div>
        <div className="flex-1 flex items-center gap-2 overflow-x-auto no-scrollbar px-4 pb-3">
          {queue.length === 0 && <div className="text-sm text-white/60">No active orders</div>}
          {queue.map(o => (
            <div key={o.id} className="bg-white/10 border border-white/10 rounded-xl px-3 py-2 min-w-[200px]" data-testid={`queue-${o.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{o.student_name}</div>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${o.status === "pending" ? "bg-amber-300 text-amber-900" : o.status === "preparing" ? "bg-blue-300 text-blue-900" : "bg-emerald-300 text-emerald-900"}`}>{o.status}</span>
              </div>
              <div className="text-[11px] text-white/70 truncate">{o.items.map(i => i.name).join(", ")}</div>
              <div className="mt-1 flex gap-1">
                {o.status === "pending" && <button data-testid={`q-prep-${o.id}`} onClick={() => updateStatus(o.id, "preparing")} className="text-[10px] px-2 py-0.5 rounded bg-blue-300 text-blue-900 font-bold">Prep</button>}
                {o.status === "preparing" && <button data-testid={`q-ready-${o.id}`} onClick={() => updateStatus(o.id, "ready")} className="text-[10px] px-2 py-0.5 rounded bg-emerald-300 text-emerald-900 font-bold">Ready</button>}
                {o.status === "ready" && <button data-testid={`q-done-${o.id}`} onClick={() => updateStatus(o.id, "completed")} className="text-[10px] px-2 py-0.5 rounded bg-white text-ink font-bold">Done</button>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {dayEnd && (
        <div data-testid="pos-day-end-modal" className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center px-4" onClick={() => setDayEnd(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-lg shadow-floating max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-brand text-xs font-bold uppercase tracking-[0.2em]"><Moon size={14} /> Day-end summary</div>
            <div className="font-heading text-3xl font-extrabold text-ink mt-2">{dayEnd.date}</div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-cream-100 rounded-xl p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500">Revenue</div>
                <div className="font-heading text-2xl font-extrabold mt-1">{inr(dayEnd.revenue)}</div>
              </div>
              <div className="bg-cream-100 rounded-xl p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500">Orders</div>
                <div className="font-heading text-2xl font-extrabold mt-1">{dayEnd.orders}</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-ink-500">By payment method</div>
              <div className="mt-2 space-y-1">
                {dayEnd.by_payment.map(p => (
                  <div key={p.method} className="flex justify-between text-sm bg-cream-100 rounded-lg p-2">
                    <span className="uppercase font-bold">{p.method}</span><span className="font-bold">{inr(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-ink-500">Top items</div>
              <div className="mt-2 space-y-1">
                {dayEnd.top_items.slice(0, 5).map(t => (
                  <div key={t.name} className="flex justify-between text-sm bg-cream-100 rounded-lg p-2">
                    <span className="font-semibold">{t.name}</span><span className="font-bold text-brand">{t.qty}×</span>
                  </div>
                ))}
              </div>
            </div>
            {dayEnd.auto_closed > 0 && <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 rounded-lg p-2">{dayEnd.auto_closed} pending order(s) auto-closed.</div>}
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="day-end-close" onClick={() => setDayEnd(null)} className="px-5 py-2.5 rounded-full bg-ink text-white font-semibold inline-flex items-center gap-2"><FileText size={14} /> Close</button>
            </div>
          </div>
        </div>
      )}

      {adhocModal && (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center px-4" onClick={() => setAdhocModal(null)} data-testid="pos-adhoc-modal">
          <div className="bg-white rounded-3xl p-7 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center"><Sparkles size={18} /></div>
              <div className="flex-1">
                <div className="font-heading text-2xl font-bold">Add ad-hoc item</div>
                <div className="text-xs text-ink-500">One-off items not in the menu — billed on the next RFID tap.</div>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Item name *</label>
                <input data-testid="adhoc-name" value={adhocModal.name} onChange={(e) => setAdhocModal({ ...adhocModal, name: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" autoFocus />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Qty</label>
                <input data-testid="adhoc-qty" type="number" min="1" value={adhocModal.qty} onChange={(e) => setAdhocModal({ ...adhocModal, qty: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Price ₹ *</label>
                <input data-testid="adhoc-price" type="number" min="0" step="0.01" value={adhocModal.price} onChange={(e) => setAdhocModal({ ...adhocModal, price: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">GST %</label>
                <input data-testid="adhoc-tax" type="number" min="0" max="28" step="0.5" value={adhocModal.tax_rate} onChange={(e) => setAdhocModal({ ...adhocModal, tax_rate: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                <div className="text-[10px] text-ink-500 mt-1">5% GST is typical for packaged canteen items. 0% for tax-free items.</div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setAdhocModal(null)} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
              <button data-testid="adhoc-save" onClick={addAdhoc} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold inline-flex items-center gap-2"><Plus size={14}/> Add to cart</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
